import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BillingCycle, BillingPlanDTO } from '@inversor-hub/types';
import { useGetMe } from '@/lib/api/me.ts';
import {
  useCreateBillingCheckoutSession,
  useCreateBillingPortalSession,
  useGetBillingCatalog,
  useGetBillingSubscription,
} from '@/lib/api/billing.ts';
import useSnackbar from '@/hooks/useSnackbar.tsx';
import { getRecommendedPlanCodeFromQuery } from '@/features/billing/upgrade-wall.ts';
import { useAuth } from '@/hooks/useAuth.ts';
import { formatBillingDate } from '@/features/billing/billing-labels.ts';

const formatMoney = (amountCents: number) =>
  new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(amountCents / 100);

const getPlanPrice = (plan: BillingPlanDTO, cycle: BillingCycle) =>
  plan.prices.find((price) => price.billingCycle === cycle);

const getVisibleFeatures = (features: string[]) => features.slice(0, 5);

const planRank = {
  STARTER: 0,
  INVERSOR: 1,
  PORTFOLIO: 2,
} as const;

const getCheckoutAlert = (checkoutStatus: string | null) => {
  if (checkoutStatus === 'cancel') {
    return {
      severity: 'info' as const,
      title: 'No se completó la suscripción',
      message: 'Puedes volver a intentarlo cuando quieras.',
    };
  }

  return null;
};

const PlansPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { data: currentUser, isLoading: isLoadingUser } = useGetMe();
  const { data: billingCatalog, isLoading: isLoadingCatalog } = useGetBillingCatalog();
  const { data: billingSubscription, isLoading: isLoadingSubscription } = useGetBillingSubscription(
    {
      enabled: currentUser?.role === 'investor',
    }
  );
  const createCheckoutSession = useCreateBillingCheckoutSession();
  const createPortalSession = useCreateBillingPortalSession();
  const { show: showSnackbar } = useSnackbar();
  const [selectedCycle, setSelectedCycle] = useState<BillingCycle>('YEARLY');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  const recommendedPlanCode = getRecommendedPlanCodeFromQuery(searchParams.get('recommended'));
  const checkoutAlert = getCheckoutAlert(searchParams.get('checkout'));

  const isPendingAction = createCheckoutSession.isPending || createPortalSession.isPending;

  const handleCheckout = async (planCode: 'INVERSOR' | 'PORTFOLIO', billingCycle: BillingCycle) => {
    try {
      const url = await createCheckoutSession.mutateAsync({ planCode, billingCycle });
      window.location.assign(url);
    } catch {
      showSnackbar({
        title: 'No pudimos iniciar el checkout',
        message: 'Inténtalo de nuevo en unos minutos.',
        variant: 'error',
      });
    }
  };

  const handleManageSubscription = async () => {
    try {
      const url = await createPortalSession.mutateAsync();
      window.location.assign(url);
    } catch {
      showSnackbar({
        title: 'No pudimos abrir el portal de Stripe',
        message: 'Inténtalo de nuevo en unos minutos.',
        variant: 'error',
      });
    }
  };

  const handleClose = () => {
    navigate('/');
  };

  if (!isAuthenticated) {
    return null;
  }

  if (isLoadingUser || !currentUser || !billingSubscription) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          backgroundImage: `radial-gradient(circle at top left, ${alpha(theme.palette.primary.main, 0.08)}, transparent 32%), radial-gradient(circle at top right, ${alpha(theme.palette.accent.main, 0.08)}, transparent 30%)`,
          py: { xs: 3, md: 5 },
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 2, md: 3 } }}>
          <Skeleton variant="rounded" height={120} sx={{ mb: { xs: 2, md: 3 }, borderRadius: 3 }} />
          <Grid container spacing={2}>
            {[0, 1, 2].map((item) => (
              <Grid
                item
                xs={12}
                md={4}
                key={item}
                sx={{ display: { xs: item > 0 ? 'none' : 'block', md: 'block' } }}
              >
                <Skeleton variant="rounded" height={420} sx={{ borderRadius: 3 }} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  }

  if (currentUser.role !== 'investor') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          bgcolor: 'background.default',
          py: 4,
        }}
      >
        <Container maxWidth="md">
          <Card variant="outlined" sx={{ borderRadius: 4 }}>
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Button onClick={handleClose} sx={{ mb: 2 }}>
                Cerrar
              </Button>
              <Alert severity="info">
                Los planes de suscripción solo están disponibles para inversores.
              </Alert>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        py: { xs: 1, md: 2 },
        pb: { xs: 3, md: 2 },
      }}
    >
      <Container maxWidth={false} sx={{ maxWidth: 1440, px: { xs: 2.5, sm: 3, md: 4 } }}>
        <Stack spacing={{ xs: 1.5, md: 2 }} sx={{ position: 'relative', pt: { xs: 1, md: 1 } }}>
          <Button
            onClick={handleClose}
            variant="text"
            color="inherit"
            sx={{
              minWidth: 40,
              minHeight: 40,
              px: 1.25,
              color: 'text.secondary',
              position: 'absolute',
              top: 0,
              right: { xs: -4, md: 0 },
              zIndex: 1,
            }}
            aria-label="Cerrar"
          >
            <CloseIcon fontSize="small" />
          </Button>

          <Stack spacing={1} alignItems="center" textAlign="center" sx={{ px: { xs: 2, md: 0 } }}>
            <Typography
              variant="h2"
              fontWeight={600}
              sx={{ fontSize: { xs: '1.6rem', sm: '2rem', md: '2.6rem' } }}
            >
              Elige tu plan
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ maxWidth: 620, fontSize: { md: '0.938rem' } }}
            >
              Tu primera inversión es gratis. Activa un plan cuando quieras gestionar toda tu
              cartera y seguir creciendo como inversor.
            </Typography>

            <ToggleButtonGroup
              value={selectedCycle}
              exclusive
              onChange={(_event, value: BillingCycle | null) => {
                if (value) {
                  setSelectedCycle(value);
                }
              }}
              sx={{
                mt: 1,
                p: 0.5,
                borderRadius: 999,
                bgcolor: alpha(theme.palette.tokens.neutrals[100], 0.8),
                '& .MuiToggleButtonGroup-grouped': {
                  border: 0,
                  borderRadius: '999px !important',
                  px: { xs: 2, md: 2.5 },
                  py: { xs: 0.75, md: 0.9 },
                  color: 'text.secondary',
                  fontWeight: 500,
                  fontSize: { xs: '0.85rem', md: '0.875rem' },
                },
                '& .Mui-selected': {
                  bgcolor: 'background.paper !important',
                  color: 'text.primary !important',
                  boxShadow: `0 1px 3px ${alpha(theme.palette.tokens.neutrals[500], 0.12)}`,
                },
              }}
            >
              <ToggleButton value="MONTHLY">Mensual</ToggleButton>
              <ToggleButton value="YEARLY">
                Anual
                <Chip
                  label="-35%"
                  size="small"
                  color="success"
                  sx={{
                    ml: 0.75,
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {checkoutAlert ? (
            <Alert
              severity={checkoutAlert.severity}
              sx={{ maxWidth: 920, mx: 'auto', width: '100%' }}
            >
              <strong>{checkoutAlert.title}</strong> {checkoutAlert.message}
            </Alert>
          ) : null}

          {billingSubscription.cancelAtPeriodEnd && billingSubscription.currentPeriodEnd ? (
            <Alert severity="warning" sx={{ maxWidth: 920, mx: 'auto', width: '100%' }}>
              Tu suscripción seguirá activa hasta el{' '}
              {formatBillingDate(billingSubscription.currentPeriodEnd)}.
            </Alert>
          ) : null}

          {isLoadingCatalog || isLoadingSubscription ? (
            <Grid container spacing={{ xs: 1.5, md: 2 }}>
              {[0, 1, 2].map((item) => (
                <Grid
                  item
                  xs={12}
                  md={4}
                  key={item}
                  sx={{ display: { xs: item > 0 ? 'none' : 'block', md: 'block' } }}
                >
                  <Skeleton variant="rounded" height={420} sx={{ borderRadius: 3 }} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Grid
              container
              spacing={{ xs: 1.5, md: 2 }}
              alignItems="stretch"
              justifyContent="center"
            >
              {billingCatalog?.map((plan) => {
                const monthlyPrice = getPlanPrice(plan, 'MONTHLY');
                const yearlyPrice = getPlanPrice(plan, 'YEARLY');
                const displayPrice = getPlanPrice(plan, selectedCycle);
                const isCurrentPlan = billingSubscription.currentPlan === plan.planCode;
                const isRecommended = recommendedPlanCode === plan.planCode;
                const paidPlanCode = plan.planCode === 'STARTER' ? undefined : plan.planCode;
                const canStartCheckout =
                  paidPlanCode !== undefined &&
                  !billingSubscription.canManageBilling &&
                  planRank[plan.planCode] > planRank[billingSubscription.currentPlan];
                const canManagePlan =
                  billingSubscription.canManageBilling && paidPlanCode !== undefined;
                const visibleFeatures = getVisibleFeatures(plan.features);
                const hiddenFeaturesCount = Math.max(
                  plan.features.length - visibleFeatures.length,
                  0
                );

                return (
                  <Grid
                    item
                    xs={12}
                    md={4}
                    key={plan.planCode}
                    sx={{ maxWidth: { xs: 480, md: 'none' }, mx: { xs: 'auto', md: 0 } }}
                  >
                    <Card
                      variant="outlined"
                      sx={{
                        height: '100%',
                        borderRadius: { xs: 3, md: 4 },
                        borderColor: isCurrentPlan
                          ? 'primary.main'
                          : isRecommended
                            ? alpha(theme.palette.primary.main, 0.5)
                            : alpha(theme.palette.tokens.neutrals[200], 1),
                        boxShadow: isCurrentPlan
                          ? `0 10px 30px ${alpha(theme.palette.primary.main, 0.12)}`
                          : 'none',
                        backgroundColor: isCurrentPlan
                          ? alpha(theme.palette.primary[50] ?? theme.palette.primary.main, 0.18)
                          : 'background.paper',
                      }}
                    >
                      <CardContent
                        sx={{
                          p: { xs: 2.5, md: 3 },
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%',
                          gap: { xs: 1.5, md: 2 },
                        }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          gap={1}
                          sx={{ minHeight: { md: 80 } }}
                        >
                          <Box>
                            <Typography
                              variant="h5"
                              fontWeight={600}
                              sx={{ mb: 0.5, fontSize: { xs: '1.15rem', md: '1.25rem' } }}
                            >
                              {plan.displayName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {plan.description}
                            </Typography>
                          </Box>

                          <Stack spacing={0.75} alignItems="flex-end" flexShrink={0}>
                            {isRecommended ? (
                              <Chip label="Recomendado" size="small" color="secondary" />
                            ) : null}
                            {isCurrentPlan ? (
                              <Chip label="Plan actual" size="small" color="primary" />
                            ) : null}
                            {isCurrentPlan && billingSubscription.entitlementSource === 'GRANT' ? (
                              <Chip label="Acceso concedido" size="small" color="success" />
                            ) : null}
                          </Stack>
                        </Box>

                        <Box sx={{ minHeight: { md: 80 } }}>
                          {displayPrice ? (
                            <Typography
                              variant="h3"
                              fontWeight={600}
                              sx={{
                                letterSpacing: '-0.03em',
                                fontSize: { xs: '1.75rem', md: '2rem' },
                              }}
                            >
                              {formatMoney(displayPrice.amountCents)}
                              <Typography component="span" variant="body1" color="text.secondary">
                                {' '}
                                / {selectedCycle === 'YEARLY' ? 'año' : 'mes'}
                              </Typography>
                            </Typography>
                          ) : (
                            <Typography
                              variant="h3"
                              fontWeight={600}
                              sx={{
                                letterSpacing: '-0.03em',
                                fontSize: { xs: '1.75rem', md: '2rem' },
                              }}
                            >
                              Gratis
                            </Typography>
                          )}

                          {monthlyPrice && yearlyPrice ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {selectedCycle === 'YEARLY' ? (
                                <>
                                  {formatMoney(Math.round(yearlyPrice.amountCents / 12))}/mes
                                  {' · '}
                                  <Typography
                                    component="span"
                                    variant="body2"
                                    color="success.main"
                                    fontWeight={600}
                                  >
                                    Ahorras{' '}
                                    {formatMoney(
                                      monthlyPrice.amountCents * 12 - yearlyPrice.amountCents
                                    )}
                                  </Typography>
                                </>
                              ) : (
                                <>
                                  {formatMoney(Math.round(yearlyPrice.amountCents / 12))}/mes si
                                  pagas anual
                                </>
                              )}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              Para siempre
                            </Typography>
                          )}
                        </Box>

                        {plan.planCode === 'STARTER' ? (
                          <Button
                            fullWidth
                            variant="outlined"
                            disabled
                            sx={{ py: { xs: 1, md: 0.75 } }}
                          >
                            {isCurrentPlan ? 'Tu plan actual' : 'Incluido'}
                          </Button>
                        ) : canStartCheckout ? (
                          <Button
                            fullWidth
                            variant="contained"
                            disabled={isPendingAction}
                            onClick={() => handleCheckout(paidPlanCode, selectedCycle)}
                            sx={{ py: { xs: 1, md: 0.75 } }}
                          >
                            Elegir {plan.displayName}
                          </Button>
                        ) : canManagePlan ? (
                          <Button
                            fullWidth
                            variant={isCurrentPlan ? 'contained' : 'outlined'}
                            disabled={isPendingAction}
                            onClick={handleManageSubscription}
                            sx={{ py: { xs: 1, md: 0.75 } }}
                          >
                            {isCurrentPlan ? 'Gestionar suscripción' : 'Cambiar plan'}
                          </Button>
                        ) : (
                          <Button
                            fullWidth
                            variant={isCurrentPlan ? 'contained' : 'outlined'}
                            disabled
                            sx={{ py: { xs: 1, md: 0.75 } }}
                          >
                            {isCurrentPlan ? 'Acceso concedido' : 'Incluido en tu acceso'}
                          </Button>
                        )}

                        <Divider />

                        <Stack spacing={0.75} sx={{ flexGrow: 1 }}>
                          {visibleFeatures.map((feature) => (
                            <Box key={feature} display="flex" alignItems="flex-start" gap={1}>
                              <CheckCircleOutlineIcon
                                sx={{ color: 'primary.main', fontSize: 16, mt: '3px' }}
                              />
                              <Typography
                                variant="body2"
                                color="text.primary"
                                sx={{ lineHeight: 1.45 }}
                              >
                                {feature}
                              </Typography>
                            </Box>
                          ))}

                          {hiddenFeaturesCount > 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ pl: 3 }}>
                              y {hiddenFeaturesCount} ventajas más
                            </Typography>
                          ) : null}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

export default PlansPage;
